/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  DefaultValues,
  FieldValues,
  useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ZodType } from 'zod';
import { FieldConfig } from './field-types';
import { FormField } from './form-field';
import { SubmitButton } from './submit-button';

interface AutoFormProps<TValues extends FieldValues> {
  fields: readonly FieldConfig<TValues>[];
  schema: ZodType<TValues>;
  defaults: DefaultValues<TValues>;
  isLoading: boolean;
  onSubmit: (values: TValues) => void;
  submitLabel?: string;
}

export function AutoForm<TValues extends FieldValues>({
  fields,
  schema,
  defaults,
  isLoading,
  onSubmit,
  submitLabel,
}: AutoFormProps<TValues>) {
  const [showApiKeys, setShowApiKeys] = useState(false);

  const form = useForm<TValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
    mode: 'onBlur',
  });

  const { register, handleSubmit, formState: { errors } } = form;

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="d-flex justify-content-end mb-2">
        <label className="form-check form-switch form-check-label small mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={showApiKeys}
            onChange={(e) => setShowApiKeys(e.target.checked)}
          />
          Ver keys de API
        </label>
      </div>

      <div className="row">
        {fields.map((field) => {
          const errorMessage = errors[field.key] ? String(errors[field.key]?.message) : undefined;
          const inputId = `field-${String(field.key)}`;
          const invalidClass = errorMessage ? 'is-invalid' : '';
          const colClassName = field.colClassName ?? 'col-md-6';

          return (
            <div key={String(field.key)} className={colClassName}>
              <FormField
                label={field.label}
                htmlFor={inputId}
                apiKey={String(field.key)}
                showApiKey={showApiKeys}
                error={errorMessage}
                hint={field.hint}
              >
                {field.kind === 'date' && (
                  <input
                    id={inputId}
                    type="date"
                    className={`form-control ${invalidClass}`}
                    {...register(field.key)}
                  />
                )}

                {field.kind === 'number' && (
                  <input
                    id={inputId}
                    type="number"
                    step={field.step ?? 'any'}
                    className={`form-control ${invalidClass}`}
                    {...register(field.key)}
                  />
                )}

                {field.kind === 'select' && (
                  <select
                    id={inputId}
                    className={`form-select ${invalidClass}`}
                    {...register(field.key)}
                  >
                    {field.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}

                {field.kind === 'custom' && field.render(form)}
              </FormField>
            </div>
          );
        })}
      </div>

      <div className="mt-3">
        <SubmitButton isLoading={isLoading} label={submitLabel} />
      </div>
    </form>
  );
}
